/**
 * Search over `summary.json`, in memory.
 *
 * BUILD-SPEC 8 calls search the single biggest gain over the workbook and
 * gives it a budget: under 100 ms, always reachable. The index is built once
 * per summary - lowercased haystacks over products, stores and receipts
 * (including their line-item names, which is what makes "yazoo" find the
 * receipts it was on) - and a query is a linear scan of a few hundred strings.
 * At 34 receipts that is microseconds; at the ~1000-receipt extrapolation in
 * BUILD-PLAN 10 it is still far inside budget, and `tests/search.test.ts`
 * holds it there.
 *
 * Searching filters and ranks; it never computes. Every figure a hit displays
 * was formatted from a field on the summary.
 */
import type { Summary } from "./contracts/index.js";
import { dateName, money } from "./format.js";

export type HitKind = "product" | "store" | "receipt";

export interface Hit {
  kind: HitKind;
  /** What navigation needs: product name, store name, or receipt id. */
  key: string;
  label: string;
  sub: string;
  score: number;
  /** Receipts carry their date so the results view can order them newest first. */
  date?: string;
}

interface Entry extends Omit<Hit, "score"> {
  /** Lowercased searchable text. */
  hay: string;
  /** Lowercased label, kept separately so prefix matches can outrank. */
  labelLower: string;
}

export interface SearchIndex {
  entries: Entry[];
}

export function buildIndex(summary: Summary): SearchIndex {
  const entries: Entry[] = [];

  for (const p of summary.products) {
    entries.push({
      kind: "product",
      key: p.name,
      label: p.name,
      sub: `${p.category} · ${p.times_bought}× bought`,
      hay: `${p.name} ${p.category} ${p.stores.join(" ")}`.toLowerCase(),
      labelLower: p.name.toLowerCase(),
    });
  }

  for (const s of summary.stores) {
    entries.push({
      kind: "store",
      key: s.name,
      label: s.name,
      sub: `${s.visits} visits · ${money(s.total, summary.base_currency)}`,
      hay: s.name.toLowerCase(),
      labelLower: s.name.toLowerCase(),
    });
  }

  for (const r of summary.receipts) {
    const itemNames = r.items ? r.items.map((i) => i.item).join(" ") : "";
    entries.push({
      kind: "receipt",
      key: r.id,
      label: `${r.store} · ${dateName(r.date)}`,
      sub: money(r.total, r.currency),
      date: r.date,
      hay: `${r.store} ${r.date} ${r.notes ?? ""} ${itemNames}`.toLowerCase(),
      labelLower: r.store.toLowerCase(),
    });
  }

  return { entries };
}

/**
 * Every term must match somewhere; ranking prefers the label, and the start
 * of the label most of all. Ties break on kind - a product called "Tesco
 * Cola" should sit under the store called "Tesco", not above it.
 */
const KIND_ORDER: Record<HitKind, number> = { store: 0, product: 1, receipt: 2 };

export function search(index: SearchIndex, query: string, limit = 25): Hit[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  const hits: Hit[] = [];
  for (const e of index.entries) {
    let score = 0;
    let ok = true;
    for (const t of terms) {
      if (!e.hay.includes(t)) {
        ok = false;
        break;
      }
      if (e.labelLower.startsWith(t)) score += 3;
      else if (e.labelLower.includes(t)) score += 2;
      else score += 1;
    }
    if (ok) hits.push({ kind: e.kind, key: e.key, label: e.label, sub: e.sub, score });
  }

  hits.sort(
    (a, b) =>
      b.score - a.score ||
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      a.label.localeCompare(b.label),
  );
  return hits.slice(0, limit);
}
